import { resolveTenantDatabaseName } from "./resolveTenantDatabaseName";
import { appendWorkspaceQuery } from "./bootstrapWorkspaceFromUrl";
import { handoffToWorkspaceBrowserState } from "./workspaceLocalStorage";
import { openWindowWithTabAuth, readTabUserData, clearTabAuthStorage } from "./tabAuthStorage";

function resolveWorkspaceSlug(bd, dominioHost) {
  return resolveTenantDatabaseName(dominioHost, bd);
}

function buildAppUrl(bd, dominioHost, targetPath) {
  const slug = resolveWorkspaceSlug(bd, dominioHost);
  if (!slug) return { slug: null, url: null };
  const path = String(targetPath || "/chat").trim() || "/chat";
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return { slug, url: appendWorkspaceQuery(normalizedPath, slug) };
}

function currentAuthScope() {
  try {
    const u = readTabUserData();
    const s = u?.scope != null ? String(u.scope).trim() : "";
    return s || "workspace";
  } catch {
    return "workspace";
  }
}

/**
 * Ativa o workspace e abre o app (portal do representante / console da plataforma).
 *
 * Padrão: **nova aba** nomeada por workspace (`xpro-ws-{slug}`), com `?ws=`.
 * A aba atual NÃO é alterada — permite várias abas, uma por cliente/workspace
 * (como no Expro 2), sem derrubar a sessão das outras.
 *
 * Partner/platform: Auth desta aba é clonada (impersonação no servidor).
 * Scope workspace: NÃO clona Bearer — força login do tenant (evita colisão de id).
 *
 * @param {import("react-router-dom").NavigateFunction} [_navigate]
 * @param {string | null | undefined} bd
 * @param {string | null | undefined} dominioHost
 * @param {string} [targetPath="/chat"]
 * @param {{ sameTab?: boolean }} [options] `sameTab: true` troca nesta aba (handoff limpo + reload)
 * @returns {Window | null}
 */
export function openWorkspaceInApp(_navigate, bd, dominioHost, targetPath = "/chat", options = {}) {
  const sameTab = Boolean(options?.sameTab);
  const { slug, url } = buildAppUrl(bd, dominioHost, targetPath);
  if (!slug || !url || typeof window === "undefined") return null;

  const scope = currentAuthScope();
  const cloneAuth = scope === "partner" || scope === "platform";
  const loginUrl = `/login?c=${encodeURIComponent(slug)}`;

  if (sameTab) {
    if (!cloneAuth) {
      try {
        clearTabAuthStorage();
      } catch {
        /* ignore */
      }
      handoffToWorkspaceBrowserState(slug);
      window.location.assign(loginUrl);
      return null;
    }
    handoffToWorkspaceBrowserState(slug);
    window.location.assign(url);
    return null;
  }

  if (cloneAuth) {
    const win = openWindowWithTabAuth(url, `xpro-ws-${slug}`);
    if (win) return win;
    // Popup bloqueado: fallback nesta aba (ainda com handoff limpo).
    handoffToWorkspaceBrowserState(slug);
    window.location.assign(url);
    return null;
  }

  // Workspace-scoped: nova aba só com ?ws= / login — sem Bearer de outro tenant.
  const win = window.open(loginUrl, `xpro-ws-${slug}`);
  if (win) {
    try {
      win.focus();
    } catch {
      /* ignore */
    }
    return win;
  }
  try {
    clearTabAuthStorage();
  } catch {
    /* ignore */
  }
  handoffToWorkspaceBrowserState(slug);
  window.location.assign(loginUrl);
  return null;
}

/** URL interna com ?ws= para abrir o tenant correto em nova aba. */
export function buildWorkspaceScopedUrl(bd, dominioHost, targetPath) {
  return buildAppUrl(bd, dominioHost, targetPath).url;
}

/**
 * Abre a solicitação em popup-call em nova janela, com workspace via ?ws=.
 * Não altera o storage da aba atual (portal/console permanece limpo).
 */
export function openWorkspaceCallPopupInNewTab(bd, dominioHost, leadId) {
  const slug = resolveWorkspaceSlug(bd, dominioHost);
  const id = leadId != null ? String(leadId).trim() : "";
  if (!slug || !id) return null;

  const url = appendWorkspaceQuery(`/popup-call/${encodeURIComponent(id)}`, slug);
  const width = window.screen.width;
  const height = window.screen.height;
  const features = `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`;

  const scope = currentAuthScope();
  if (scope === "partner" || scope === "platform") {
    return openWindowWithTabAuth(url, `ConversationPopup-${slug}-${id}`, features);
  }
  // Operador workspace: precisa já estar logado nesse tenant; abre URL (auth da aba não é clonada).
  const win = window.open(url, `ConversationPopup-${slug}-${id}`, features);
  return win || null;
}

/** Abre rota do workspace em nova aba, com ?ws=. Não altera o storage da aba atual. */
export function openWorkspacePathInNewTab(bd, dominioHost, targetPath) {
  const url = buildWorkspaceScopedUrl(bd, dominioHost, targetPath);
  if (!url) return null;
  const scope = currentAuthScope();
  if (scope === "partner" || scope === "platform") {
    return openWindowWithTabAuth(url, `xpro-ws-path`);
  }
  return window.open(url, "_blank");
}
